import { useCallback, useEffect, useMemo, useState } from "react";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import {
  getAntiForgeryToken,
  getUnpublishedResidenceTypeWithNoImageList,
  ResidenceTypeModel,
  uploadImageForResidenceType,
} from "../../clients/BackOfficeApi";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { PaginationForm } from "../../elements/PaginationForm";
import { ImageInput } from "../ImageInput";
import {
  ArrowRightIcon,
  AttachmentIcon,
  WarningTwoIcon,
} from "@chakra-ui/icons";
import { LinkButton } from "../../elements/LinkButton";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { Banner } from "../Banner";

const ignoreResidenceTypeWithoutImageAtom = atomWithStorage<boolean>(
  "ignoreResidenceTypesWithoutImage",
  false,
  createJSONStorage(() => sessionStorage),
);

export const ResidenceTypesWithoutImageModal = withPagination(
  { settingsKey: "residenceTypes" },
  () => {
    const [residenceTypes, setResidenceTypes] = useState<ResidenceTypeModel[]>(
      [],
    );
    const { onOpen, isOpen, onClose } = useDisclosure();

    const isAuthenticated = useIsAuthenticated();
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const { limit, offset } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);
    const [tableSpan, setTableSpan] = useState(1);

    const fetchApplicableResidenceTypes = useCallback(
      async (signal?: AbortSignal, refetchAll = false) => {
        if (!isAuthenticated) return;

        setLoading(true);

        const query = refetchAll
          ? { Offset: 0, Limit: offset + limit }
          : { Offset: offset, Limit: limit };

        getUnpublishedResidenceTypeWithNoImageList({
          query,
          signal,
          throwOnError: true,
        })
          .then(({ data }) => {
            if (signal?.aborted) return;

            const items = data?.items ?? [];

            const loadedItems = items.length;
            setLoadedOffset(query.Offset + loadedItems);
            setResidenceTypes((prev) => {
              prev.splice(query.Offset, loadedItems, ...items);
              return prev.slice(0, query.Offset + loadedItems);
            });
            setTotal(data?.totalCount ?? 0);
          })
          .catch((e) => {
            if (signal?.aborted) return;

            throw e;
          })
          .finally(() => setLoading(false));
      },
      [offset, limit, isAuthenticated],
    );

    useEffect(() => {
      const abortController = new AbortController();

      fetchApplicableResidenceTypes(abortController.signal);

      return () => abortController.abort("Component unmounted or re-rendered");
    }, [fetchApplicableResidenceTypes]);

    const postUploadResidenceTypeImage = useCallback(
      async (residenceType: ResidenceTypeModel, file: File) => {
        try {
          const { data: antiForgeryToken } = await getAntiForgeryToken({
            credentials: "include",
            throwOnError: true,
          });

          await uploadImageForResidenceType({
            path: { residenceTypeId: residenceType.id },
            body: { file },
            headers: {
              "X-XSRF-TOKEN": antiForgeryToken,
            },
            credentials: "include",
            throwOnError: true,
          });

          fetchApplicableResidenceTypes(undefined, true);
        } catch {
          /* eslint-disable-line no-empty */
        }
      },
      [fetchApplicableResidenceTypes],
    );

    const moreRowsAvailable = useMemo(
      () => loadedOffset < total,
      [loadedOffset, total],
    );

    return loading || !total ? null : (
      <>
        <Banner
          atom={ignoreResidenceTypeWithoutImageAtom}
          leftIcon={<WarningTwoIcon />}
          onClick={onOpen}
        >
          Er zijn {total} ongepubliceerde woningtypes zonder afbeelding.{" "}
          <ArrowRightIcon h={3} ml={2} verticalAlign="baseline" />
        </Banner>
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Onvolledige woningtypes</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb={4}>
                <Text>
                  Onderstaande woningtypes hebben geen afbeelding, zonder
                  afbeelding kunnen ze niet worden gepubliceerd.
                </Text>
              </Box>
              <Table variant="striped" w="full">
                <Thead>
                  <Tr
                    ref={(el: HTMLTableRowElement | null) =>
                      setTableSpan(el?.childElementCount ?? 1)
                    }
                  >
                    <Th>Naam</Th>
                    <Th>Upload</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {residenceTypes.map((residenceType) => (
                    <Tr key={residenceType.id}>
                      <Td>
                        <LinkButton to={`/residence-types/${residenceType.id}`}>
                          {residenceType.name}
                        </LinkButton>
                      </Td>
                      <Td py={0}>
                        <ImageInput
                          onChange={(files) =>
                            postUploadResidenceTypeImage(
                              residenceType,
                              files[0],
                            )
                          }
                        >
                          <AttachmentIcon mr={1} />
                          Selecteer afbeelding
                        </ImageInput>
                      </Td>
                    </Tr>
                  ))}
                  {moreRowsAvailable && (
                    <Tr>
                      <Td colSpan={tableSpan}>
                        <PaginationForm
                          loading={loading}
                          offset={loadedOffset}
                        />
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </ModalBody>
            <ModalFooter />
          </ModalContent>
        </Modal>
      </>
    );
  },
);
